import type { CSSProperties, HTMLAttributes } from 'react';
import { kaiFontFamily } from '../../../../core/tokens';
import { kaiTypographyWeb } from '../../../typography';
import { ReviewFlowMediaCell } from '../patterns/ReviewFlowMediaCell';
import { REVIEW_COLORS, REVIEW_RADII, REVIEW_SPACING } from '../reviewFlowTokens';
import type {
  ReviewFlowCompareGridCellModel,
  ReviewFlowCompareGridColumnModel,
  ReviewFlowCompareGridModel,
  ReviewFlowCompareGridRowModel,
} from '../reviewFlowTypes';
import {
  resolveStyleOverride,
  type StyleOverride,
} from '../styleOverrides';

export interface ReviewFlowCompareGridProps extends HTMLAttributes<HTMLDivElement> {
  model: ReviewFlowCompareGridModel;
  onSelectCell?: (id: string) => void;
  onSelectColumn?: (id: string) => void;
  onToggleWalk?: (id: string) => void;
  columnMinWidth?: number;
  cellAspectRatio?: string;
  styleOverride?: StyleOverride;
}

const makeGridTemplateColumns = (columnCount: number, columnMinWidth: number): string =>
  `repeat(${Math.max(columnCount, 1)}, minmax(${columnMinWidth}px, 1fr))`;

const getCellForColumn = (
  row: ReviewFlowCompareGridRowModel,
  column: ReviewFlowCompareGridColumnModel,
): ReviewFlowCompareGridCellModel =>
  row.cells.find(cell => cell.columnId === column.id) ?? {
    id: `${row.id}:${column.id}:empty`,
    columnId: column.id,
    walkId: row.walk.id,
    title: column.title,
    subtitle: column.subtitle,
    empty: true,
  };

const columnHeaderBaseStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  minWidth: 0,
  minHeight: 40,
  padding: `${REVIEW_SPACING.xSmall}px ${REVIEW_SPACING.medium}px`,
  border: `1px solid ${REVIEW_COLORS.border}`,
  borderRadius: REVIEW_RADII.small,
  background: REVIEW_COLORS.surface,
  color: REVIEW_COLORS.text,
  textAlign: 'center',
};

const getColumnHeaderStyle = (column: ReviewFlowCompareGridColumnModel): CSSProperties => ({
  ...columnHeaderBaseStyle,
  borderColor: column.selected || column.current ? REVIEW_COLORS.borderHeavy : REVIEW_COLORS.border,
  background: column.current ? REVIEW_COLORS.surfaceMuted : REVIEW_COLORS.surface,
});

const getColumnButtonStyle = (interactive: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  width: '100%',
  minWidth: 0,
  border: 0,
  background: 'transparent',
  color: 'inherit',
  cursor: interactive ? 'pointer' : 'default',
  fontFamily: 'inherit',
  padding: 0,
  textAlign: 'center',
});

const titleStyle: CSSProperties = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  ...kaiTypographyWeb.smallMed,
};

const subtitleStyle: CSSProperties = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: REVIEW_COLORS.textMuted,
  ...kaiTypographyWeb.xxSmallBold,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

export const ReviewFlowCompareGrid = ({
  model,
  onSelectCell,
  onSelectColumn,
  onToggleWalk,
  columnMinWidth = 240,
  cellAspectRatio = '305 / 207',
  styleOverride: compareGridCss,
  style,
  ...props
}: ReviewFlowCompareGridProps) => {
  const gridTemplateColumns = makeGridTemplateColumns(model.columns.length, columnMinWidth);

  return (
    <div
      data-review-flow-compare-grid='true'
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: REVIEW_SPACING.large,
        minWidth: 0,
        width: '100%',
        fontFamily: kaiFontFamily,
        ...style,
        ...resolveStyleOverride(compareGridCss),
      }}
      {...props}
    >
      <div
        data-review-flow-compare-grid-columns='true'
        style={{
          display: 'grid',
          gridTemplateColumns,
          gap: REVIEW_SPACING.medium,
          minWidth: 0,
        }}
      >
        {model.columns.map(column => (
          <div
            key={column.id}
            data-review-flow-compare-grid-column='true'
            data-review-flow-column-id={column.id}
            data-review-flow-current={column.current ? 'true' : 'false'}
            data-review-flow-selected={column.selected ? 'true' : 'false'}
            style={getColumnHeaderStyle(column)}
          >
            <button
              type='button'
              onClick={() => onSelectColumn?.(column.id)}
              style={getColumnButtonStyle(Boolean(onSelectColumn))}
            >
              <span style={titleStyle}>{column.title}</span>
              {column.subtitle ? <span style={subtitleStyle}>{column.subtitle}</span> : null}
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: REVIEW_SPACING.xLarge, minWidth: 0 }}>
        {model.rows.map(row => (
          <section
            key={row.id}
            data-review-flow-compare-grid-row='true'
            data-review-flow-row-id={row.id}
            data-review-flow-walk-id={row.walk.id}
            data-review-flow-current={row.walk.current ? 'true' : 'false'}
            style={{ display: 'flex', flexDirection: 'column', gap: REVIEW_SPACING.small, minWidth: 0 }}
          >
            <div
              data-review-flow-compare-grid-row-header='true'
              style={{ display: 'flex', alignItems: 'center', gap: REVIEW_SPACING.small, minWidth: 0 }}
            >
              <button
                type='button'
                aria-label={`Toggle ${row.walk.label}`}
                onClick={() => onToggleWalk?.(row.walk.id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: REVIEW_SPACING.small,
                  minWidth: 0,
                  border: 0,
                  background: 'transparent',
                  color: REVIEW_COLORS.text,
                  cursor: onToggleWalk ? 'pointer' : 'default',
                  fontFamily: 'inherit',
                  padding: 0,
                  textAlign: 'left',
                }}
              >
                <span aria-hidden='true' style={{ color: REVIEW_COLORS.text, ...kaiTypographyWeb.small }}>
                  {row.walk.expanded === false ? '›' : '⌄'}
                </span>
                <span style={kaiTypographyWeb.smallBold}>{row.walk.label}</span>
              </button>

              {row.walk.meta ? <span style={{ color: REVIEW_COLORS.textMuted, ...kaiTypographyWeb.xSmall }}>{row.walk.meta}</span> : null}

              {row.walk.conductedBy ? <span style={{ color: REVIEW_COLORS.textSoft, ...kaiTypographyWeb.xSmall }}>{row.walk.conductedBy}</span> : null}
            </div>

            {row.walk.expanded === false ? null : (
              <div
                data-review-flow-compare-grid-row-cells='true'
                style={{
                  display: 'grid',
                  gridTemplateColumns,
                  gap: REVIEW_SPACING.medium,
                  minWidth: 0,
                }}
              >
                {model.columns.map(column => {
                  const cell = getCellForColumn(row, column);
                  return (
                    <div
                      key={`${row.id}-${column.id}`}
                      data-review-flow-compare-grid-cell='true'
                      data-review-flow-cell-id={cell.id}
                      data-review-flow-column-id={column.id}
                      data-review-flow-walk-id={row.walk.id}
                      data-review-flow-column-selected={column.selected ? 'true' : 'false'}
                      data-review-flow-empty={cell.empty || (!cell.tiles?.length && !cell.imageUrl) ? 'true' : 'false'}
                      style={{
                        minWidth: 0,
                        borderRadius: REVIEW_RADII.small,
                        border: `1px solid ${column.selected ? REVIEW_COLORS.borderHeavy : 'transparent'}`,
                        background: column.selected ? REVIEW_COLORS.surfaceMuted : 'transparent',
                      }}
                    >
                      {cell.tiles?.length
                        ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: REVIEW_SPACING.medium, minWidth: 0 }}>
                            {cell.tiles.map(tile => (
                              <ReviewFlowMediaCell
                                key={tile.id}
                                model={tile}
                                aspectRatio={cellAspectRatio}
                                onSelect={onSelectCell}
                              />
                            ))}
                          </div>
                        )
                        : (
                          <ReviewFlowMediaCell
                            model={cell}
                            aspectRatio={cellAspectRatio}
                            onSelect={onSelectCell}
                          />
                        )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};
